import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiGet, apiPost } from '../api/client';
import cantalLogo from '../assets/cantal.png';

interface CompetitionDto {
  id: string;
  slug: string;
  name: string;
}

interface MatchDto {
  id: string;
  externalId: string;
  round: string | null;
  homeClubName: string;
  homeClubShortName: string;
  awayClubName: string;
  awayClubShortName: string;
  kickoffUtc: string | null;
  homeScore: number | null;
  awayScore: number | null;
  status: string;
}

interface ScrapeJobDto {
  id: string;
  status: string;
  progressDone: number;
  progressTotal: number | null;
  errors: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

function formatKickoff(kickoffUtc: string | null): string {
  if (!kickoffUtc) return 'Date inconnue';
  const d = new Date(kickoffUtc);
  return d.toLocaleString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  });
}

function groupByRound(matches: MatchDto[]): Map<string, MatchDto[]> {
  const map = new Map<string, MatchDto[]>();
  for (const m of matches) {
    const key = m.round ?? 'Sans journée';
    const arr = map.get(key) ?? [];
    arr.push(m);
    map.set(key, arr);
  }
  return map;
}

function findNextMatchId(matches: MatchDto[]): string | null {
  const now = new Date();
  return (
    matches.find(m => m.status !== 'Finished' && m.kickoffUtc !== null && new Date(m.kickoffUtc) >= now)?.id ??
    matches.find(m => m.status !== 'Finished')?.id ??
    null
  );
}

function MatchRow({ match, isNext, innerRef, onClick }: {
  match: MatchDto;
  isNext: boolean;
  innerRef?: React.Ref<HTMLDivElement> | undefined;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const finished = match.status === 'Finished';
  return (
    <div ref={innerRef}>
      {isNext && (
        <div style={{ padding: '0.3rem 1.25rem', background: '#eff6ff', borderBottom: '1px solid #dbeafe', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1d4ed8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Prochain match</span>
        </div>
      )}
      <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: '0.875rem 1.25rem',
          display: 'grid',
          gridTemplateColumns: '1fr 120px 1fr',
          alignItems: 'center',
          cursor: 'pointer',
          background: isNext ? (hovered ? '#eff6ff' : '#f8faff') : (hovered ? '#f8fafc' : '#fff'),
          transition: 'background 0.1s',
        }}
      >
        <span style={{ textAlign: 'right', fontWeight: finished ? 600 : isNext ? 600 : 400, fontSize: '0.93rem', color: '#111827', paddingRight: '0.75rem' }}>
          {match.homeClubShortName}
        </span>
        <div style={{ textAlign: 'center' }}>
          {finished && match.homeScore !== null && match.awayScore !== null ? (
            <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e3a5f', letterSpacing: '0.05em' }}>
              {match.homeScore} — {match.awayScore}
            </span>
          ) : (
            <span style={{ fontSize: '0.78rem', color: isNext ? '#2563eb' : '#9ca3af', fontWeight: isNext ? 600 : 400, lineHeight: 1.4 }}>
              {match.kickoffUtc ? formatKickoff(match.kickoffUtc) : 'à venir'}
            </span>
          )}
        </div>
        <span style={{ fontWeight: finished ? 600 : isNext ? 600 : 400, fontSize: '0.93rem', color: '#111827', paddingLeft: '0.75rem' }}>
          {match.awayClubShortName}
        </span>
      </div>
    </div>
  );
}

export default function CalendarPage(): JSX.Element {
  const navigate = useNavigate();
  const { user, logout, accessToken } = useAuth();
  const [competitions, setCompetitions] = useState<CompetitionDto[]>([]);
  const [matches, setMatches] = useState<MatchDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [proD2, setProD2] = useState<CompetitionDto | null>(null);
  const [scrapeJob, setScrapeJob] = useState<ScrapeJobDto | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [nextMatchId, setNextMatchId] = useState<string | null>(null);
  const nextMatchRef = useRef<HTMLDivElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJob = useCallback((jobId: string) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const job = await apiGet<ScrapeJobDto>(`/scrape-jobs/${jobId}`);
          setScrapeJob(job);
          if (job.status === 'Succeeded' || job.status === 'Failed' || job.status === 'Partial') {
            stopPolling();
            setRefreshing(false);
            const comps = await apiGet<CompetitionDto[]>('/competitions');
            const found = comps.find(c => c.slug === 'pro-d2' || c.name.toLowerCase().includes('pro d2'));
            if (found) {
              setProD2(found);
              const updated = await apiGet<MatchDto[]>(`/competitions/${found.id}/matches`);
              setMatches(updated);
              setNextMatchId(findNextMatchId(updated));
            }
          }
        } catch {
          stopPolling();
          setRefreshing(false);
        }
      })();
    }, 2000);
  }, [stopPolling, proD2]);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  useEffect(() => {
    if (nextMatchId && nextMatchRef.current) {
      setTimeout(() => {
        nextMatchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [nextMatchId]);

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const comps = await apiGet<CompetitionDto[]>('/competitions');
        setCompetitions(comps);
        const proD2Comp = comps.find(c => c.slug === 'pro-d2' || c.name.toLowerCase().includes('pro d2'));
        if (proD2Comp) {
          setProD2(proD2Comp);
          const m = await apiGet<MatchDto[]>(`/competitions/${proD2Comp.id}/matches`);
          setMatches(m);
          setNextMatchId(findNextMatchId(m));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    try {
      setRefreshing(true);
      setScrapeJob(null);
      const url = proD2
        ? `/competitions/${proD2.id}/calendar/refresh`
        : `/competitions/sync`;
      const job = await apiPost<ScrapeJobDto>(url);
      setScrapeJob(job);
      pollJob(job.id);
    } catch (err) {
      setRefreshing(false);
      setError(err instanceof Error ? err.message : 'Erreur lors du rafraîchissement');
    }
  }, [proD2, refreshing, pollJob]);

  const grouped = groupByRound(matches);
  const progressPct = scrapeJob?.progressTotal
    ? Math.round((scrapeJob.progressDone / scrapeJob.progressTotal) * 100)
    : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <nav style={{
        background: '#1e3a5f',
        color: '#fff',
        padding: '0 1.75rem',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src={cantalLogo} alt="Pierroinfo" style={{ height: '40px', width: 'auto' }} />
          <span style={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>Pierroinfo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', fontSize: '0.875rem' }}>
          <span style={{ color: '#a8c4e0', fontWeight: 500 }}>{user?.displayName}</span>
          {user?.role === 'Admin' && (
            <NavBtn onClick={() => navigate('/admin')} variant="purple">Admin</NavBtn>
          )}
          <NavBtn onClick={() => void logout()} variant="ghost">Déconnexion</NavBtn>
        </div>
      </nav>

      <main style={{ maxWidth: '860px', margin: '0 auto', padding: '2rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.2rem', color: '#0f172a' }}>Calendrier Pro D2</h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '0.875rem' }}>Liste des matchs</p>
          </div>
          {accessToken && !loading && (
            <button
              onClick={() => void handleRefresh()}
              disabled={refreshing}
              style={{
                background: refreshing ? '#e2e8f0' : '#1e3a5f',
                color: refreshing ? '#94a3b8' : '#fff',
                border: 'none',
                padding: '0.55rem 1.1rem',
                borderRadius: '8px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
              }}
            >
              {refreshing ? 'Collecte en cours…' : 'Collecte de données'}
            </button>
          )}
        </div>

        {scrapeJob && <ScrapeProgress job={scrapeJob} progressPct={progressPct} />}

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem', border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '3rem', textAlign: 'center', color: '#94a3b8', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            Chargement…
          </div>
        )}

        {!loading && !error && matches.length === 0 && (
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '3rem', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <p style={{ fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>Aucun match disponible</p>
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', margin: 0 }}>
              {competitions.length === 0
                ? 'Aucune compétition trouvée. Utilisez le bouton Rafraîchir.'
                : 'Utilisez le bouton Rafraîchir pour charger les matchs.'}
            </p>
          </div>
        )}

        {!loading && matches.length > 0 && Array.from(grouped.entries()).map(([round, roundMatches]) => (
          <section key={round} style={{ marginBottom: '1.75rem' }}>
            <div style={{ marginBottom: '0.625rem' }}>
              <span style={{
                fontSize: '0.8rem',
                fontWeight: 700,
                color: '#fff',
                background: '#1e3a5f',
                padding: '0.3rem 0.75rem',
                borderRadius: '99px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}>
                {round}
              </span>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              {roundMatches.map((match, idx) => {
                const isNext = match.id === nextMatchId;
                return (
                  <div key={match.id} style={{ borderTop: idx > 0 ? '1px solid #f1f5f9' : undefined }}>
                    <MatchRow
                      match={match}
                      isNext={isNext}
                      innerRef={isNext ? nextMatchRef : undefined}
                      onClick={() => navigate(`/matches/${match.id}`)}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}

function ScrapeProgress({ job, progressPct }: { job: ScrapeJobDto; progressPct: number | null }) {
  const isRunning = job.status === 'Queued' || job.status === 'Running';
  const isSuccess = job.status === 'Succeeded';
  const isError = job.status === 'Failed' || job.status === 'Partial';

  const borderColor = isError ? '#fca5a5' : isSuccess ? '#86efac' : '#bfdbfe';
  const bgColor = isError ? '#fef2f2' : isSuccess ? '#f0fdf4' : '#eff6ff';
  const textColor = isError ? '#b91c1c' : isSuccess ? '#15803d' : '#1d4ed8';

  const statusText = () => {
    if (job.status === 'Queued') return 'Collecte en file d\'attente…';
    if (job.status === 'Running') {
      const done = job.progressDone;
      const total = job.progressTotal;
      if (total) return `Collecte en cours : ${done} / ${total} matchs traités`;
      return `Collecte en cours : ${done} matchs traités…`;
    }
    if (job.status === 'Succeeded') return 'Collecte terminée — données à jour.';
    if (job.status === 'Partial') return `Collecte partielle — certains matchs n'ont pas pu être récupérés.`;
    if (job.status === 'Failed') return `Échec de la collecte${job.errors ? ` : ${job.errors}` : '.'}`;
    return '';
  };

  return (
    <div style={{ marginBottom: '1.25rem', background: bgColor, borderRadius: '10px', border: `1px solid ${borderColor}`, padding: '1rem 1.125rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: progressPct !== null ? '0.625rem' : 0 }}>
        {isRunning && (
          <span style={{ display: 'inline-block', width: '14px', height: '14px', borderRadius: '50%', border: '2px solid #93c5fd', borderTopColor: '#1d4ed8', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
        )}
        {isSuccess && <span style={{ color: '#16a34a', fontSize: '1rem', flexShrink: 0 }}>✓</span>}
        {isError && <span style={{ color: '#dc2626', fontSize: '1rem', flexShrink: 0 }}>✕</span>}
        <span style={{ fontSize: '0.875rem', color: textColor, fontWeight: 500 }}>{statusText()}</span>
      </div>

      {progressPct !== null && (
        <div>
          <div style={{ background: isError ? '#fecaca' : isSuccess ? '#bbf7d0' : '#bfdbfe', borderRadius: '99px', height: '6px', overflow: 'hidden' }}>
            <div style={{
              background: isError ? '#ef4444' : isSuccess ? '#22c55e' : '#2563eb',
              width: `${progressPct}%`,
              height: '100%',
              transition: 'width 0.4s ease',
              borderRadius: '99px',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem', fontSize: '0.75rem', color: textColor, opacity: 0.8 }}>
            <span>{job.progressDone} matchs</span>
            <span>{progressPct}%</span>
          </div>
        </div>
      )}

      {isError && job.errors && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#b91c1c', opacity: 0.85 }}>{job.errors}</p>
      )}
    </div>
  );
}

function NavBtn({ onClick, variant, children }: { onClick: () => void; variant: 'ghost' | 'purple'; children: React.ReactNode }) {
  const [hovered, setHovered] = useState(false);
  const base: React.CSSProperties = {
    background: 'transparent',
    border: `1px solid ${variant === 'purple' ? (hovered ? '#a78bfa' : '#7c3aed') : (hovered ? '#cbd5e1' : '#a8c4e0')}`,
    color: variant === 'purple' ? (hovered ? '#ddd6fe' : '#c4b5fd') : (hovered ? '#e2e8f0' : '#a8c4e0'),
    padding: '0.3rem 0.75rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    transition: 'border-color 0.15s, color 0.15s',
  };
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={base}>
      {children}
    </button>
  );
}
